/**
 * Theme Store
 * 
 * Manages dark/light mode with system preference detection
 * and localStorage persistence using Zustand.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

/**
 * Apply theme to document
 * NOTE: Dark mode is currently disabled - always forces light mode
 */
const applyTheme = (_theme: Theme) => {
  const root = window.document.documentElement;
  
  // Always remove dark class and force light mode
  root.classList.remove('dark');
  root.classList.add('light');
  
  // Disable dark mode completely
  // const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  // if (theme === 'system') {
  //   root.classList.add(systemDark ? 'dark' : 'light');
  // } else {
  //   root.classList.add(theme);
  // }
};

/**
 * Theme store with persistence
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      
      setTheme: (theme: Theme) => {
        set({ theme });
        applyTheme(theme);
      },
      
      toggleTheme: () => {
        const current = get().theme;
        const newTheme: Theme = current === 'light' ? 'dark' : 'light';
        set({ theme: newTheme });
        applyTheme(newTheme);
      },
    }),
    {
      name: 'yim-theme',
      onRehydrateStorage: () => (state) => {
        // Apply theme when store is rehydrated from storage
        if (state) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

/**
 * Initialize theme on app load
 * Call this once in your app entry point
 * NOTE: Dark mode is currently disabled - always forces light mode
 */
export const initializeTheme = () => {
  // Force light mode regardless of stored theme
  applyTheme('light');
  
  // Disable system theme change listener
  // const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  // mediaQuery.addEventListener('change', () => {
  //   const currentTheme = useThemeStore.getState().theme;
  //   if (currentTheme === 'system') {
  //     applyTheme('system');
  //   }
  // });
};

/**
 * Hook to get the resolved theme (not 'system')
 */
export const useResolvedTheme = () => {
  const theme = useThemeStore((state) => state.theme);
  
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  
  return theme;
};

export default useThemeStore;

